<template>
  <div class="section">
    <div class="container" style="max-width: 640px;">
      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding: 3rem 0;">
        <div class="loading-spinner" style="margin: 0 auto;"></div>
      </div>

      <!-- Not Found -->
      <div v-else-if="!order" class="empty-state card">
        <h3 class="empty-title">ไม่พบคำสั่งซื้อ</h3>
        <router-link to="/member/profile" class="btn btn-primary mt-4">กลับไปหน้าบัญชี</router-link>
      </div>

      <div v-else>
        <!-- Back Button -->
        <router-link to="/member/profile" class="back-link mb-4">
          <svg class="back-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          กลับไปหน้าบัญชี
        </router-link>

        <!-- Order Header -->
        <div class="card">
          <div class="card-body">
            <div class="order-header">
              <div>
                <h1 class="order-number">คำสั่งซื้อ #{{ order.order_number }}</h1>
                <p class="order-date">{{ formatDate(order.created_at) }}</p>
              </div>
              <span class="badge" :class="getStatusClass(order.status)">
                {{ getStatusText(order.status) }}
              </span>
            </div>

            <!-- Cancellation Reason -->
            <div v-if="order.status === 'cancelled' && order.cancel_reason" class="cancellation-box">
              <div class="cancellation-header">
                <svg class="cancellation-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <span class="cancellation-title">เหตุผลที่ยกเลิก</span>
              </div>
              <p class="cancellation-reason">{{ order.cancel_reason }}</p>
            </div>
          </div>
        </div>

        <!-- Shipping Info -->
        <div class="card mt-4">
          <div class="card-body">
            <h2 class="section-title">ข้อมูลจัดส่ง</h2>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">ชื่อผู้รับ:</span>
                <span class="detail-value">{{ order.customer_name }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">โทรศัพท์:</span>
                <span class="detail-value">{{ order.phone }}</span>
              </div>
              <div class="detail-item full-width">
                <span class="detail-label">ที่อยู่:</span>
                <span class="detail-value">{{ order.address }}</span>
              </div>
            </div>

            <!-- Tracking Info -->
            <div v-if="order.shipping?.tracking_number" class="tracking-box mt-4">
              <h3 class="tracking-title">ข้อมูลการจัดส่ง</h3>
              <p class="tracking-number">เลขพัสดุ: {{ order.shipping.tracking_number }}</p>
              <p v-if="order.shipping.company_name" class="shipping-company">
                ขนส่ง: {{ order.shipping.company_name }}
              </p>
              <div v-if="order.shipping.parcel_image_url || order.shipping.parcel_image_path" class="parcel-image-section mt-3">
                <p class="detail-label">รูปพัสดุ:</p>
                <a :href="getImageUrl(order.shipping.parcel_image_url || order.shipping.parcel_image_path)" target="_blank">
                  <img :src="getImageUrl(order.shipping.parcel_image_url || order.shipping.parcel_image_path)" alt="รูปพัสดุ" class="parcel-image">
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="card mt-4">
          <div class="card-body">
            <h2 class="section-title">รายการสินค้า</h2>
            <div class="items-list">
              <div v-for="item in order.items" :key="item.id" class="order-item">
                <div class="item-info">
                  <p class="item-name">{{ item.product_name }}</p>
                  <p v-if="item.variant_info" class="item-variant">{{ item.variant_info }}</p>
                  <p class="item-qty">จำนวน: {{ item.quantity }}</p>
                </div>
                <div class="item-price">{{ formatPrice(item.total_price) }}</div>
              </div>
            </div>

            <div class="order-summary">
              <div class="summary-row">
                <span>ยอดรวมสินค้า</span>
                <span>{{ formatPrice(order.subtotal) }}</span>
              </div>
              <div class="summary-row">
                <span>ค่าจัดส่ง</span>
                <span>{{ formatPrice(order.shipping_fee) }}</span>
              </div>
              <div class="summary-row total">
                <span>ยอดรวมทั้งหมด</span>
                <span>{{ formatPrice(order.total_amount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Action -->
        <div v-if="order.status === 'pending_payment'" class="card mt-4">
          <div class="card-body text-center">
            <p class="mb-4">กรุณาชำระเงินเพื่อยืนยันคำสั่งซื้อ</p>
            <router-link :to="`/order/${order.order_number}`" class="btn btn-primary btn-lg">
              แจ้งชำระเงิน
            </router-link>
          </div>
        </div>

        <!-- Payment Slip -->
        <div v-if="order.payment_slip" class="card mt-4">
          <div class="card-body">
            <h2 class="section-title">หลักฐานการชำระเงิน</h2>
            <div class="slip-info">
              <a :href="getImageUrl(order.payment_slip.image_path)" target="_blank">
                <img :src="getImageUrl(order.payment_slip.image_path)" alt="สลิปการโอนเงิน" class="slip-image">
              </a>
              <div class="slip-details">
                <p><span class="detail-label">วันที่โอน:</span> {{ order.payment_slip.transfer_date }}</p>
                <p><span class="detail-label">เวลาที่โอน:</span> {{ order.payment_slip.transfer_time }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '../api';
import { useNotification } from '../composables/useNotification';
import { getImageUrl } from '../utils/imageUrl';
import { getOrderStatusLabel } from '../utils/orderStatus';

const route = useRoute();
const { showError } = useNotification();

const order = ref(null);
const loading = ref(true);

onMounted(async () => {
  try {
    const response = await api.get(`/member/orders/${route.params.orderNumber}`);
    order.value = response.data.data;
  } catch (error) {
    showError('ไม่สามารถโหลดข้อมูลคำสั่งซื้อได้');
  } finally {
    loading.value = false;
  }
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(price);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('th-TH');
};

const getStatusClass = (status) => {
  const classes = {
    'pending_payment': 'badge-warning',
    'pending_verification': 'badge-warning',
    'paid': 'badge-info',
    'processing': 'badge-warning',
    'shipped': 'badge-success',
    'delivered': 'badge-success',
    'cancelled': 'badge-danger'
  };
  return classes[status] || 'badge-warning';
};

const getStatusText = (status) => getOrderStatusLabel(status);
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-light);
  text-decoration: none;
  font-size: 0.9375rem;
  transition: color 0.2s;
}
.back-link:hover { color: var(--color-primary); }
.back-icon { width: 1.25rem; height: 1.25rem; }
.mb-4 { margin-bottom: 1rem; }
.mt-3 { margin-top: 0.75rem; }
.mt-4 { margin-top: 1rem; }

.card { background: var(--color-surface); border-radius: 0.75rem; box-shadow: var(--shadow-sm); }
.card-body { padding: 1.5rem; }

.order-header { display: flex; justify-content: space-between; align-items: flex-start; }
.order-number { font-size: 1.25rem; font-weight: 700; color: var(--color-primary); margin: 0; }
.order-date { font-size: 0.875rem; color: var(--color-text-muted); margin-top: 0.25rem; }

.cancellation-box {
  margin-top: 1rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
}
.cancellation-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
.cancellation-icon { width: 1.25rem; height: 1.25rem; color: #dc2626; }
.cancellation-title { font-weight: 600; color: #dc2626; }
.cancellation-reason { color: #7f1d1d; font-size: 0.9375rem; margin: 0; }

.section-title { font-size: 1.125rem; font-weight: 600; color: var(--color-text); margin-bottom: 1rem; }

.detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
.detail-item { display: flex; flex-direction: column; gap: 0.25rem; }
.detail-item.full-width { grid-column: span 2; }
.detail-label { font-size: 0.8125rem; color: var(--color-text-muted); }
.detail-value { font-size: 0.9375rem; color: var(--color-text); }

.tracking-box { padding: 1rem; background: #fef3c7; border-radius: 0.5rem; }
.tracking-title { font-weight: 600; color: var(--color-text); margin-bottom: 0.5rem; }
.tracking-number { font-size: 1rem; font-weight: 600; color: var(--color-text); }
.shipping-company { font-size: 0.875rem; color: var(--color-text-light); }

.parcel-image, .slip-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 0.5rem;
  border: 1px solid var(--color-border);
  margin-top: 0.5rem;
  cursor: pointer;
  transition: opacity 0.2s;
}
.parcel-image:hover, .slip-image:hover { opacity: 0.9; }

.items-list { display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1rem; }
.order-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.75rem;
  background: var(--color-bg);
  border-radius: 0.5rem;
}
.item-name { font-weight: 500; color: var(--color-text); margin: 0; }
.item-variant { font-size: 0.8125rem; color: var(--color-text-muted); margin: 0.25rem 0 0; }
.item-qty { font-size: 0.8125rem; color: var(--color-text-light); margin: 0.25rem 0 0; }
.item-price { font-weight: 600; color: var(--color-text); white-space: nowrap; }

.order-summary { border-top: 1px solid var(--color-border-light); padding-top: 1rem; }
.summary-row { display: flex; justify-content: space-between; padding: 0.5rem 0; font-size: 0.9375rem; }
.summary-row.total { font-weight: 700; font-size: 1.125rem; color: var(--color-primary); border-top: 1px solid var(--color-border-light); margin-top: 0.5rem; padding-top: 1rem; }

.slip-info { display: flex; flex-direction: column; gap: 1rem; }
.slip-details p { margin: 0.25rem 0; font-size: 0.9375rem; }

.text-center { text-align: center; }
.empty-state { text-align: center; padding: 3rem 1rem; }
.empty-title { font-size: 1.25rem; font-weight: 600; color: var(--color-text); }

@media (max-width: 640px) {
  .detail-grid { grid-template-columns: 1fr; }
  .detail-item.full-width { grid-column: span 1; }
}
</style>
